import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Button } from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";
import {
  makeFieldConfigDynamic,
  saleFieldConfiguration,
  saleInitialValues,
} from "../../../formConfigs/Fleet/saleFieldConfig";
import useAuth from "../../../hooks/useAuth";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import TrailerPopup from "../TrailerPopup/TrailerPopup";
import TruckPopup from "../TruckPopup/TruckPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

export const fetchAssets = async (
  selectedAssetType: string,
  token: string | null | undefined
) => {
  const endpoint =
    selectedAssetType === "Truck" ? "getAllTrucks" : "getAllTrailers";
  const response = await axios.get(`${apiVariable}/${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return response.data.map((item: any) => ({
    label: `${item.unitNumber} | ${item.make} ${item.model} ${item.year}`,
    id: item.id.toString(),
  }));
};

const SalePopup = () => {
  const [open, setOpen] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState<string>("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: assetData,
    isError,
    error,
  } = useQuery({
    queryKey: ["assets", selectedAssetType],
    queryFn: () => fetchAssets(selectedAssetType, token),
    enabled: !!selectedAssetType,
  });

  useEffect(() => {
    if (isError && error) {
      setStatus("error");
      setErrorMessage(
        (error as any).response?.data || "Failed to fetch assets"
      );
    }
  }, [isError, error]);

  const saleMutation = useMutation({
    mutationFn: (data: any) => {
      return axios.post(`${apiVariable}/sale`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      setStatus("success");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["sales"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit sale");
    },
  });

  const submitHandler = (data: any) => {
    const submitData = {
      ...data,
      truckId: selectedAssetType === "Truck" ? getId(data.assetNumber) : null,
      trailerId:
        selectedAssetType === "Trailer" ? getId(data.assetNumber) : null,
    };
    delete submitData.assetAffected;
    delete submitData.assetNumber;
    saleMutation.mutate(submitData);
  };

  const onValuesChange = (values: any) => {
    const assetType = values.assetAffected;
    setSelectedAssetType(assetType);
  };

  const dynamicFields = makeFieldConfigDynamic(
    saleFieldConfiguration,
    setSelectedAssetType,
    selectedAssetType
  );

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Create Sale
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={saleInitialValues}
        title="Sale Form"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        autocompleteData={{ assets: assetData }}
        loading={saleMutation.isPending}
        onValuesChange={onValuesChange}
        fieldComponents={{
          assetNumber: (
            <Box display="flex" justifyContent="center" alignItems="center">
              {selectedAssetType === "Trailer" ? (
                <TrailerPopup />
              ) : (
                <TruckPopup />
              )}
            </Box>
          ),
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage="Sale added successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default SalePopup;
